if (!customElements.get('bleusalt-pdp-discovery')) {
  class BleusaltPdpDiscovery extends HTMLElement {
    connectedCallback() {
      if (this.refreshTimer) return;
      this.counts = {complementary: null, related: null};
      this.activeIntent = 'complementary';
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.onClick = event => {
        const tab = event.target.closest('[role="tab"]');
        if (tab && this.contains(tab)) this.select(tab.dataset.intent);
      };
      this.onKeyDown = event => {
        const tab = event.target.closest('[role="tab"]');
        if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        const visible = this.tabs.filter(button => !button.hidden);
        const index = visible.indexOf(tab);
        let next = event.key === 'Home' ? 0 : event.key === 'End' ? visible.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + visible.length) % visible.length;
        event.preventDefault();
        if (visible[next]) { this.select(visible[next].dataset.intent); visible[next].focus(); }
      };
      this.onVisibility = () => { if (!document.hidden) this.refresh(); };
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeyDown);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.refreshTimer = window.setInterval(() => { if (!document.hidden) this.refresh(); }, 30000);
      this.refresh();
    }

    disconnectedCallback() {
      window.clearInterval(this.refreshTimer);
      this.refreshTimer = null;
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeyDown);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.controllers?.forEach(controller => controller.abort());
    }

    select(intent) {
      if (!Object.hasOwn(this.counts, intent) || this.counts[intent] === 0) return;
      this.activeIntent = intent;
      this.renderState();
    }

    renderState() {
      if (this.counts[this.activeIntent] === 0) {
        const available = Object.keys(this.counts).find(intent => this.counts[intent] > 0);
        if (available) this.activeIntent = available;
      }
      const empty = Object.values(this.counts).every(count => count === 0);
      this.dataset.hasRecommendations = String(!empty);
      this.dataset.activeIntent = this.activeIntent;
      this.hidden = empty;
      this.tabs.forEach(tab => {
        const selected = tab.dataset.intent === this.activeIntent;
        tab.hidden = this.counts[tab.dataset.intent] === 0;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      this.panels.forEach(panel => { panel.hidden = panel.dataset.intent !== this.activeIntent; });
    }

    clearExpired() {
      for (const panel of this.panels) {
        const until = Number(panel.dataset.guardUntil);
        if (until && until <= Date.now() / 1000) {
          panel.replaceChildren();
          this.counts[panel.dataset.intent] = 0;
        }
      }
      this.renderState();
    }

    async refresh() {
      if (!this.isConnected || this.loading) return;
      this.clearExpired();
      if (this.panels.some(panel => panel.contains(document.activeElement))) return;
      this.loading = true;
      this.controllers = [];
      try { await Promise.all(this.panels.map(panel => this.loadPanel(panel))); }
      finally { this.loading = false; this.controllers = []; }
    }

    async loadPanel(panel) {
      const controller = new AbortController();
      this.controllers.push(controller);
      const timeout = window.setTimeout(() => controller.abort(), 12000);
      try {
        const url = new URL(this.dataset.url, location.origin);
        url.searchParams.set('intent', panel.dataset.intent);
        url.searchParams.set('_refresh', Math.floor(Date.now() / 30000));
        const response = await fetch(url, {cache: 'no-store', signal: controller.signal});
        if (!response.ok) throw new Error('Discovery unavailable');
        const html = document.createElement('div');
        html.innerHTML = await response.text();
        const result = html.querySelector(`[data-discovery-result="${panel.dataset.intent}"]`);
        if (!result) throw new Error('Discovery response missing');
        if (!this.isConnected) return;
        const expired = Number(result.dataset.guardUntil) > 0 && Number(result.dataset.guardUntil) <= Date.now() / 1000;
        if (panel.contains(document.activeElement) && !expired) return;
        const count = expired ? 0 : result.querySelectorAll('.bs-discovery__item').length;
        panel.innerHTML = count ? result.innerHTML : '';
        panel.dataset.guardUntil = result.dataset.guardUntil || '0';
        panel.setAttribute('aria-busy', 'false');
        this.counts[panel.dataset.intent] = count;
        this.renderState();
        if (typeof lazyImages === 'function') lazyImages();
      } catch (error) {
        if (!this.isConnected) return;
        panel.replaceChildren();
        panel.setAttribute('aria-busy', 'false');
        this.counts[panel.dataset.intent] = 0;
        this.renderState();
      } finally { window.clearTimeout(timeout); }
    }
  }
  customElements.define('bleusalt-pdp-discovery', BleusaltPdpDiscovery);
}
